import type { ReactNode } from 'react'
import { Stack, CommandBar, Text } from '@fluentui/react'
import type { ICommandBarItemProps } from '@fluentui/react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import { useAuth } from '../hooks/useAuth'
import { useState } from 'react'
import HelpDialog from './HelpDialog'

interface LayoutProps {
  children: ReactNode
}

const Layout = ({ children }: LayoutProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { instance } = useMsal()
  const { isAdmin } = useAuth()
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false)

  const handleLogout = () => {
    instance.logoutRedirect()
  }

  const handleHelpClick = () => {
    setIsHelpDialogOpen(true)
  }

  const handleHelpDialogDismiss = () => {
    setIsHelpDialogOpen(false)
  }

  const commandBarItems: ICommandBarItemProps[] = [
    {
      key: 'dashboard',
      text: 'Dashboard',
      iconProps: { iconName: 'ViewDashboard' },
      onClick: () => { navigate('/') },
      className: location.pathname === '/' ? 'is-selected' : undefined,
    },
    {
      key: 'files',
      text: 'File Manager',
      iconProps: { iconName: 'FolderHorizontal' },
      onClick: () => { navigate('/files') },
      className: location.pathname.startsWith('/files') ? 'is-selected' : undefined,
    },
    // Admin-only navigation item
    ...(isAdmin() ? [{
      key: 'settings',
      text: 'Settings',
      iconProps: { iconName: 'Settings' },
      onClick: () => { navigate('/settings') },
      className: location.pathname === '/settings' ? 'is-selected' : undefined,
    }] : []),
  ]

  const commandBarFarItems: ICommandBarItemProps[] = [
    {
      key: 'help',
      text: 'Help',
      iconProps: { iconName: 'Help' },
      onClick: () => { handleHelpClick() },
    },
    {
      key: 'logout',
      text: 'Sign Out',
      iconProps: { iconName: 'SignOut' },
      onClick: () => { handleLogout() },
    },
  ]

  return (
    <Stack styles={{ root: { minHeight: '100vh' } }}>
      {/* Header */}
      <Stack 
        horizontal 
        horizontalAlign="space-between"
        verticalAlign="center" 
        styles={{ 
          root: { 
            padding: '0 32px', 
            height: '65px', 
            backgroundColor: '#ffffff',
            borderBottom: '1px solid #edebe9',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
          } 
        }}
      >
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 16 }}>
          <img 
            src="https://cdn.net-cloud.com/api/files/resize?path=Logos%2Fnt_logo_horiz.png&width=242&height=45" 
            alt="NT CDN Logo" 
            style={{ 
              height: '45px', 
              width: '242px', 
              objectFit: 'contain',
              cursor: 'pointer'
            }}
            onClick={() => navigate('/')}
            onError={(e) => {
              // Fallback to text if logo fails to load
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const fallback = target.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
          <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 8 }} styles={{ root: { alignSelf: 'flex-end', paddingBottom: '7px' } }}>
            <Text variant="xLarge" styles={{ root: { fontWeight: 600, color: '#323130', lineHeight: '1' } }}>
              CDN
            </Text>
            <Text variant="medium" styles={{ root: { color: '#605e5c', marginLeft: 8, lineHeight: '1' } }}>
              Content Distribution Network
            </Text>
          </Stack>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 16 }} style={{ display: 'none' }}>
            <Text variant="xxLarge" styles={{ root: { fontWeight: 700, color: '#0078d4' } }}>
              NT
            </Text>
            <Text variant="xLarge" styles={{ root: { fontWeight: 600, color: '#323130' } }}>
              CDN
            </Text>
            <Text variant="medium" styles={{ root: { color: '#605e5c', marginLeft: 8 } }}>
              Content Distribution Network
            </Text>
          </Stack>
        </Stack>
        
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
          <Text variant="medium" styles={{ root: { color: '#605e5c' } }}>
            Powered by Net-Cloud
          </Text>
        </Stack>
      </Stack>

      {/* Navigation */}
      <CommandBar
        items={commandBarItems}
        farItems={commandBarFarItems}
        styles={{
          root: {
            padding: '0 32px',
            backgroundColor: '#f8f9fa',
            borderBottom: '1px solid #edebe9',
          },
        }}
      />

      {/* Main Content */}
      <Stack 
        grow 
        styles={{ 
          root: { 
            padding: '32px',
            overflow: 'auto',
            backgroundColor: '#f8f9fa',
            minHeight: 'calc(100vh - 185px)'
          } 
        }}
        className="page-content"
      >
        {children}
      </Stack>

      {/* Footer */}
      <Stack 
        horizontal 
        horizontalAlign="space-between" 
        verticalAlign="center"
        styles={{ 
          root: { 
            padding: '16px 32px',
            backgroundColor: '#ffffff',
            borderTop: '1px solid #edebe9',
            minHeight: '60px'
          } 
        }}
      >
        <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
          © 2025 Networking Technologies - Your Global Technology Resource
        </Text>
        <Stack horizontal tokens={{ childrenGap: 16 }}>
          <Text variant="small" styles={{ root: { color: '#0078d4', cursor: 'pointer' } }}>
            Support
          </Text>
          <Text variant="small" styles={{ root: { color: '#0078d4', cursor: 'pointer' } }}>
            Privacy
          </Text>
          <Text variant="small" styles={{ root: { color: '#0078d4', cursor: 'pointer' } }}>
            Terms
          </Text>
        </Stack>
      </Stack>

      {/* Help Dialog */}
      <HelpDialog 
        isOpen={isHelpDialogOpen} 
        onDismiss={handleHelpDialogDismiss} 
      />
    </Stack>
  )
}

export default Layout